/**
 * document-app.ts
 * Aplicación Vue 3 para gestión de documentos (facturas, compras, cotizaciones, etc.)
 *
 * Esta app recibe datos iniciales desde el backend mediante un objeto global window.DOCUMENT_CONTEXT,
 * que se define en la plantilla `documento_form.html`.
  */

import * as bootstrap from 'bootstrap';
import Decimal from "decimal.js";
import { defineComponent } from 'vue';
import { Articulo, Document, DocumentLite, FormaDePago, Moneda, Movement, Persona, Transaction, ValidationError } from '../../models.ts';
import { apiAxios, formatCurrency, intcomma, notifyMe, parseErrors } from '../../utils.ts';
import vars from '../../vars.ts';
import ListPagination from '../components/ListPagination.vue';
import TableHeaderSort from '../components/TableHeaderSort.vue';


declare global {
  interface Window {
    DOCUMENT_CONTEXT: {
      csrfToken: string;
      id: number | null;
      doc_type_class: string;
      htmlUrls: {
        documentoAppList: string;
      };
      apiUrls: {
        documento: string;
        documentoPrint: string;
        documentoSendMail: string;
        articulo: string;
        movimiento: string;
        transaccion: string;
        persona: string;
        moneda: string;
        formaDePago: string;
        dgiiRequestEncf: string;
        attachedForm: string;
        attachedDelete: string;
      };
      user: {
        id: number;
        username: string;
        // Permisos del usuario. La estructura exacta depende de los permisos asignados en el backend.
        // Aquí definimos un objeto los permisos específicos usados en esta app.
        permissions: {
          documento: string[];
          contabilidad: string[];
          inventario: string[];
          dgii: string[];
          articulo: string[];
        }
      };
      t: { [key: string]: string }  // Traducciones

    };
    DocumentFormApp: any;
  }
}


/** * Función de traducción simple.
 * Busca la clave en el objeto de traducciones y retorna el valor.
 * Si no se encuentra, retorna la clave misma.
 */
function t(key: string, capfirst: boolean = false): string {
  let translation = window.DOCUMENT_CONTEXT.t[key] || key;
  if (capfirst) {
    translation = translation.charAt(0).toUpperCase() + translation.slice(1);
  }
  return translation;
}


const DocumentFormApp = defineComponent({
  components: {
    'table-header-sort': TableHeaderSort,
    'list-pagination': ListPagination,
  },

  data() {
    return {
      vars: vars,
      isMounted: false,
      pollingInterval: null as number | null,

      activeView: 'list' as 'list' | 'detail',
      context: window.DOCUMENT_CONTEXT,
      doc_type_class_lower: window.DOCUMENT_CONTEXT.doc_type_class.toLowerCase(),  // Solo para conveniencia

      document: null as Document | null,
      documentPendingServerSave: false,
      documentUpdatingFromBackend: false,  // Flag para evitar guardar automáticamente mientras se actualiza desde el backend
      documentLoading: false,
      documentSavingDebounceTimer: null as number | null,
      documentSaving: false,
      documentErrors: {} as Record<string, any>,
      documentMovementsFetchProgress: null as number | null,
      documentMovementsFetchProgressInterval: null as number | null,

      documentEmailSending: false,

      movementErrors: {} as Record<string, any>,
      selected_movement: null as Movement | null,

      articulos: [] as any[] | null,
      articulos_search_query: '',
      articulos_search_query_debounced: '',
      articulos_search_debounce_timer: null as number | null,
      articulos_search_max_results: 50,
      articulos_only_active: true,
      articulos_only_available: false,

      personas: [] as Persona[] | null,
      personas_search_query: '',
      personas_search_loading: false,
      personas_search_debounce_timer: null as number | null,

      // Indica si los precios de los artículos incluyen impuestos.
      // Antes se calculaba en el backend, pero ahora este solo recibe precios sin impuestos.
      pricesIncludeTax: false,

      monedas: [] as Moneda[] | null,

      formas_de_pago: [] as FormaDePago[] | null,

      suggestions: [] as any[],
      suggestionsLoading: false,
      showSuggestions: false,

      cancelDocumentSaving: false,  // Indica si se está guardando la anulación del documento
      cancelDocumentReason: '',
      cancelDocumentItems: [] as {
        invoiceMovement: Movement;  // Referencia al movimiento original de la factura
        cantidad: number;
        disponible: number;  // Cantidad disponible para anular
      }[],
      cancelDocumentSubtotal: new Decimal(0),
      cancelDocumentDiscount: new Decimal(0),
      cancelDocumentTax: new Decimal(0),
      cancelDocumentTotal: new Decimal(0),

      // Datos para el listado de documentos

      list_search: null as string | null, // Consulta de búsqueda global
      list_search_loading: false,
      list_search_debounce_timer: null as number | null,
      listOrdering: [] as string[],  // e.g. ['-fecha', 'numero']
      listFilter: {
        page_size: 10 as number,
        page: 1 as number,
        fecha_from: null as string | null,
        fecha_to: null as string | null,
        numero: null as string | null,
        tipo: null as string | null,
        //tipo_clase: null as string | null,  No se usa porque ya se filtra en el backend según la URL
        persona: null as number | null,
        almacen: null as number | null,
        create_user: null as number | null,
        ncf: null as number | null,
        tipo_comprobante_fiscal: null as number | null,
      },
      listPagination: {
        count: 0,
        next: null as string | null,
        previous: null as string | null,
        results: [] as Document[],
      },
      listPaginationPageSizes: [5, 10, 20, 50, 100],
      listPaginationPageSize: 10,  // Debe coincidir con el backend
      listPaginationMaxPageSize: 100,  // Debe coincidir con el backend
      listPaginationPageQueryParam: 'page',  // Debe coincidir con el backend
      listPaginationPageSizeQueryParam: 'page_size',  // Debe coincidir con el backend
      listConfigAllColumns: [
        {
          key: 'secuencia',
          label: t('número', true),
          cssClass: 'text-start',
        },
        {
          key: 'ncf__numero',
          label: t('comprobante', true),
          cssClass: 'text-start',
        },
        {
          key: 'fecha',
          label: t('fecha', true),
          cssClass: 'text-start',
        },
        {
          key: 'fecha_vence',
          label: t('vence', true),
          cssClass: 'text-start',
        },
        {
          key: 'persona__identification',
          label: t('identificación', true),
          cssClass: 'text-start',
        },
        {
          key: 'persona__firstname',
          label: t('nombre', true),
          cssClass: 'text-start',
        },
        {
          key: 'moneda__simbolo',
          label: t('moneda', true),
          cssClass: 'text-center',
        },
        {
          key: 'q_impuesto',
          label: t('impuesto', true),
          cssClass: 'text-end',
        },
        {
          key: 'q_total',
          label: t('total', true),
          cssClass: 'text-end',
        },
        {
          key: 'q_total_local',
          label: t('total local', true),
          cssClass: 'text-end',
        },
        {
          key: 'balance',
          label: t('balance', true),
          cssClass: 'text-end',
        },
      ],
      listConfigVisibleColumns: [
        'secuencia',
        'ncf__numero',
        'fecha',
        'fecha_vence',
        'persona__identification',
        'persona__firstname',
        'q_impuesto',
        'q_total',
      ],

    }
  },

  async created() {
    // Cargar preferencia desde localStorage
    this.loadLocalStoragePreferences();

    // Cargar filtros desde la URL
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of params.entries()) {
      if (key in this.listFilter) {
        const current = this.listFilter[key as keyof typeof this.listFilter];
        this.listFilter[key as keyof typeof this.listFilter] = typeof current === 'number' ? Number(value) : value;
      }

      if (key === 'search') this.list_search = value;
    }

  },

  async mounted() {
    await this.fetchInitialData();
    this.isMounted = true;

    // Iniciar polling para realizar operaciones periódicas
    this.pollingInterval = setInterval(async () => {
      await this.pollingTask();
    }, 20000);  // Cada 20 segundos
  },

  beforeUnmount() {
    clearInterval(this.pollingInterval!);
  },

  watch: {

    /**
     * Vigila los cambios en los filtros de listado para recargar los datos.
     */
    listOrdering: {
      async handler() {
        await this.fetchDocumentList();
      },
      deep: true,
    },

    listFilter: {
      async handler() {
        // await this.fetchDocumentList();  No necesario, ya se maneja en applyListFilters
      },
      deep: true,
    },

    /**
     * Vigila los cambios en listConfigVisibleColumns para guardar en localStorage.
     */
    listConfigVisibleColumns: {
      handler() {
        this.saveListConfigVisibleColumnsToLocalStorage();
      },
      deep: true,
    },

    /**
     * Vigila los cambios en la consulta de búsqueda de artículos para aplicar debounce.
     */
    articulos_search_query(newVal: string) {
      if (this.articulos_search_debounce_timer) {
        clearTimeout(this.articulos_search_debounce_timer);
      }
      this.articulos_search_debounce_timer = window.setTimeout(() => {
        this.articulos_search_query_debounced = newVal;
      }, 800);
    },

  },

  computed: {

    listFilteredColumns(): Array<{ key: string; label: string }> {
      return this.listConfigAllColumns.filter(col => this.listConfigVisibleColumns.includes(col.key));
    },

    /**
     * Indica si hay filtros activos en el listado de documentos.
     * Incluye el filtro de búsqueda global.
     */
    hasActiveListFilters(): boolean {
      for (const [key, value] of Object.entries(this.listFilter)) {
        if (value !== null && value !== '') {
          return true;
        }
      }
      if (this.list_search && this.list_search.trim() !== '') {
        return true;
      }
      return false;
    },

    /**
     * Indica si se puede agregar un nuevo documento del tipo actual.
     */
    canAdd(): boolean {
      if (!this.context.user.permissions.documento) return false;
      return (
        this.context.user.permissions.documento.includes('add_documento') &&
        this.context.user.permissions.documento.includes(`add_documento_${this.doc_type_class_lower}`)
      )
    },

    /**
     * Indica si se puede modificar el documento actual.
     */
    canChange(): boolean {
      if (!this.document) return false;

      // Si ya fue solicitado el NCF electrónico, no se puede editar
      if (this.document.ncf && this.document.ncf.status) return false;

      // El usuario debe tener permiso
      if (!this.context.user.permissions.documento) return false;
      if (
        !this.context.user.permissions.documento.includes('change_documento') ||
        !this.context.user.permissions.documento.includes(`change_documento_${this.doc_type_class_lower}`)
      ) {
        return false;
      }

      // Si está en estado borrador, se puede editar
      return this.document.is_borrador;
    },

    /**
     * Indica si se puede eliminar el documento actual.
     */
    canDelete(): boolean {
      if (!this.document) return false;

      // Si ya fue solicitado el NCF electrónico, no se puede eliminar
      if (this.document.ncf && this.document.ncf.status) return false;

      // El usuario debe tener permiso
      if (!this.context.user.permissions.documento) return false;
      if (
        !this.context.user.permissions.documento.includes('delete_documento') ||
        !this.context.user.permissions.documento.includes(`delete_documento_${this.doc_type_class_lower}`)
      ) {
        return false;
      }

      // Si está en estado borrador, se puede eliminar
      return this.document.is_borrador;
    },

    /**
     * Indica si se puede anular el documento actual (crear una devolución).
     */
    canCancel(): boolean {
      if (!this.document) return false;

      // El usuario debe tener permiso
      if (!this.context.user.permissions.documento) return false;
      if (!this.context.user.permissions.documento.includes('add_documento_devolucion')) return false;

      // No se puede anular si no tiene NCF electrónico finalizado y secuencia consumida
      if (
        !this.document.ncf ||
        !this.document.ncf.status ||
        !this.document.ncf.status.sequence_consumed
      ) {
        return false;
      }

      // El documento debe permitir crear devoluciones
      return this.document.canCreateReturn;
    },

    canViewMovements(): boolean {
      if (!this.document) return false;

      // El usuario debe tener permiso
      if (!this.context.user.permissions.inventario) return false;
      return this.context.user.permissions.inventario.includes('view_movimiento');
    },

    canAddMovement(): boolean {
      if (!this.document) return false;

      // El documento debe poder ser editado
      if (!this.canChange) return false;

      // El usuario debe tener permiso de agregar movimientos y de ver artículos
      if (!this.context.user.permissions.inventario) return false;
      if (!this.context.user.permissions.articulo) return false;
      return (
        this.context.user.permissions.inventario.includes('add_movimiento') &&
        this.context.user.permissions.articulo.includes('view_articulo')
      );
    },

    // NOTE: Hay una función `canChangeThisMovement` usada en la plantilla
    // para validar cada movimiento individualmente.
    canChangeMovement(): boolean {
      if (!this.document) return false;

      // El documento debe poder ser editado
      if (!this.canChange) return false;

      // El usuario debe tener permiso
      if (!this.context.user.permissions.inventario) return false;
      return this.context.user.permissions.inventario.includes('change_movimiento');
    },

    canDeleteMovement(): boolean {
      if (!this.document) return false;

      // El documento debe poder ser editado
      if (!this.canChange) return false;

      // El usuario debe tener permiso
      if (!this.context.user.permissions.inventario) return false;
      return this.context.user.permissions.inventario.includes('delete_movimiento');
    },

    canViewPayments(): boolean {
      if (!this.document) return false;

      // El usuario debe tener permiso
      if (!this.context.user.permissions.contabilidad) return false;
      return this.context.user.permissions.contabilidad.includes('view_transaccion');
    },

    canAddPayment(): boolean {
      if (!this.document) return false;

      // El usuario debe tener permiso de agregar transacciones
      if (!this.context.user.permissions.contabilidad) return false;
      return this.context.user.permissions.contabilidad.includes('add_transaccion');
    },

    canChangePayment(): boolean {
      if (!this.document) return false;

      // El usuario debe tener permiso
      if (!this.context.user.permissions.contabilidad) return false;
      return this.context.user.permissions.contabilidad.includes('change_transaccion');
    },

    canDeletePayment(): boolean {
      if (!this.document) return false;

      // El usuario debe tener permiso
      if (!this.context.user.permissions.contabilidad) return false;
      return this.context.user.permissions.contabilidad.includes('delete_transaccion');
    },

    /**
     * Indica si se puede solicitar el NCF electrónico para el documento actual.
     */
    canRequestElectronicDocument(): boolean {
      if (!this.document) return false;

      // El tipo de documento debe soportar NCF electrónico
      if (!this.document.tipo_data.is_electronic) return false;

      // El usuario debe tener permiso
      if (!this.context.user.permissions.dgii) return false;
      if (!this.context.user.permissions.dgii.includes('add_dgiidocumentstatus')) return false;

      // No se puede solicitar si ya fue finalizado correctamente y la secuencia fue consumida
      if (
        this.document.ncf &&
        this.document.ncf.status &&
        this.document.ncf.status.internal_status === vars.FINISHED &&
        this.document.ncf.status.sequence_consumed
      ) {
        return false;
      }

      // No se puede solicitar si el documento ya tiene una solicitud en proceso
      if (
        this.document.ncf &&
        this.document.ncf.status &&
        this.document.ncf.status.internal_status === vars.REQUESTED
      ) {
        return false;
      }

      // Solo se puede solicitar si el documento tiene valor
      return this.document.q_total > 0;
    },

    // Para mantener compatibilidad con documentos convencionales
    canRequestConventionalDocument(): boolean {
      if (!this.document) return false;

      // El tipo de documento no soporta NCF electrónico
      if (this.document.tipo_data.is_electronic) return false;

      // El usuario debe tener permiso de impresión
      if (!this.context.user.permissions.documento) return false;
      if (!this.context.user.permissions.documento.includes('print_documento')) return false;

      // Solo se puede solicitar si el documento tiene valor
      return this.document.q_total > 0;
    },

    canSendEmail(): boolean {
      if (!this.document) return false;
      // El usuario debe tener permiso
      if (!this.context.user.permissions.documento) return false;
      return true; // TODO: Uso futuro return this.context.user.permissions.documento.includes('send_email_documento');
    },

    /**
     * Filtra los artículos según la consulta de búsqueda.
     * Retorna solo los artículos que coinciden con el código o descripción.
     * Si no hay consulta, retorna un array vacío.
     * Esto se usa para mostrar sugerencias al buscar artículos.
     */
    filterArticulos(): any[] {
      if (!this.articulos || !this.articulos_search_query_debounced) return [];

      const q = this.articulos_search_query_debounced.toLowerCase();
      const out: any[] = [];

      for (const art of this.articulos) {
        if (art._search.includes(q)) {
          out.push(art);
        }
        if (out.length >= this.articulos_search_max_results) break;
      }
      return out;
    },

    /**
     * Indica si el formulario está deshabilitado (no se puede editar).
     * Basado en permisos y estado de carga/guardado.
     */
    isFormDisabled(): boolean {
      return !this.canChange || this.documentLoading || this.documentSaving;
    },

  },

  methods: {

    t,  // Función de traducción
    intcomma,
    formatCurrency,

    /**
     * Navega a la URL dada, preservando los filtros activos del listado.
     * @param url URL base a la que navegar.
     * @return void
     */
    goToUrl(url: string, openInNewTab: boolean = false): void {
      if (this.hasActiveListFilters) {
        const params = new URLSearchParams();

        // Agregar filtros activos
        for (const [key, value] of Object.entries(this.listFilter)) {
          if (value !== null && value !== '') {
            params.append(key, String(value));
          }
        }

        // Agregar búsqueda global
        if (this.list_search && this.list_search.trim() !== '') {
          params.append('search', this.list_search.trim());
        }

        const queryString = params.toString();
        if (url.includes('?')) {
          url += '&' + queryString;
        } else {
          url += '?' + queryString;
        }
      }

      if (openInNewTab) {
        window.open(url, '_blank');
      } else {
        window.location.href = url;
      }
    },

    async pollingTask() {
      // Hacer ping al backend para ver si el documento necesita ser recargado
      console.debug('Polling task started.');
      if (
        this.document &&
        this.document.id &&
        this.document.is_borrador &&
        this.activeView === 'detail' &&
        !this.documentLoading &&
        !this.documentSaving &&
        !this.documentUpdatingFromBackend
      ) {
        try {
          const response = await apiAxios.get(this.context!.apiUrls.documento + this.document.id + '/ping/');
          const update_date = response.data.update_date;

          console.debug('Polling document ping response:', response.data, 'current document update_date:', this.document.update_date);

          if (update_date && this.document.update_date !== update_date) {
            this.documentUpdatingFromBackend = true;
            await this.fetchDocument();
            this.documentUpdatingFromBackend = false;
          }
        } catch (error) {
          console.error('Error during polling document ping:', error);
        }
      } else {
        console.debug('Polling skipped: no document loaded or not in draft state.');
      }

      // Vigilar el estado del NCF electrónico para ver si es necesario recargar el documento
      if (
        this.document &&
        this.document.ncf &&
        this.document.ncf.status &&
        !this.document.is_borrador &&
        !this.documentUpdatingFromBackend
      ) {
        if (this.document.ncf.status.internal_status === vars.REQUESTED) {
          console.debug('Polling detected REQUESTED status in document.ncf.status, refetching document from backend.');
          this.documentUpdatingFromBackend = true;
          await this.fetchDocument();
          this.documentUpdatingFromBackend = false;
          // Emitir una notificación al usuario si el estado es FINISHED
          if (
            this.document.ncf.status.internal_status === vars.FINISHED &&
            this.document.ncf.status.sequence_consumed
          ) {
            notifyMe({
              title: 'NCF Electrónico Generado',  // TODO: i18n
              body: `Se ha generado el comprobante electrónico ${this.document.ncf.numero} para el documento ${this.document.numero}.`,  // TODO: i18n
              action: () => { window.open(this.document!.ncf!.status!.document_stamp_url!, '_blank'); },
              timeout: 10000,
            });
          }
        }
      }

    },

    /**
     * Carga los datos iniciales necesarios para la aplicación.
     * Si se proporciona un ID de documento en el contexto, carga ese documento.
     * Si no, carga el listado de documentos.
     */
    async fetchInitialData(): Promise<void> {

      if (this.context?.id) {
        this.activeView = 'detail';

        await this.fetchMonedas();
        await this.fetchFormasDePago();

        // Cargar el documento. Primero completo (en paralelo), luego ligero (para campos básicos)
        // Esto es para optimizar la carga inicial y evitar retrasos en la interfaz.
        this.fetchDocument(this.context!.id!);
        await this.fetchDocumentLite(this.context!.id!);

        // En documentos de salida, solo mostrar artículos disponibles o de servicio
        this.articulos_only_available = this.document!.tipo_data.inventario == this.vars.SALIDA ? true : false;

        // Solo cargar estos datos si se puede editar el documento
        if (this.canChange) {
          this.fetchArticulos();
        }

      } else {
        this.activeView = 'list';
        await this.fetchDocumentList();
      }
    },

    async fetchMonedas() {
      try {
        const response = await apiAxios.get(this.context!.apiUrls.moneda, {
          params: {
            limit: 1000, // La API no tiene límites, pero por si acaso se implementa en el futuro ponemos un número alto
          }
        });
        const data = response.data.results ? response.data.results : response.data;
        this.monedas = data.map((data: any) => new Moneda(data));
      } catch (error) {
        // Es posible que el backend bloquee el acceso por falta de permisos
        console.warn('Error fetching monedas:', error);
        this.monedas = [];
      }
    },

    async fetchFormasDePago() {
      try {
        const response = await apiAxios.get(this.context!.apiUrls.formaDePago, {
          params: {
            activo: true,
            limit: 1000, // La API no tiene límites, pero por si acaso se implementa en el futuro ponemos un número alto
          }
        });
        const data = response.data.results ? response.data.results : response.data;
        this.formas_de_pago = data.map((data: any) => new FormaDePago(data));
      } catch (error) {
        // Es posible que el backend bloquee el acceso por falta de permisos
        console.warn('Error fetching formas de pago:', error);
        this.formas_de_pago = [];
      }
    },

    async fetchArticulos() {
      const response = await apiAxios.get(this.context!.apiUrls.articulo, {
        params: {
          only_active: this.articulos_only_active,
          only_available: this.articulos_only_available,
          almacen: this.document?.almacen || null,  // Usado para anotar disponible y latest_price
          limit: 10000000,  // La API no tiene límites, pero por si acaso se implementa en el futuro ponemos un número alto
        }
      });
      const data = response.data.results ? response.data.results : response.data;
      this.articulos = data.map((data: any) => {
        data._search = (data.codename + ' ' + data.description).toLowerCase();  // Campo auxiliar para búsquedas
        return data;  // No usar new Articulo(data) para optimizar memoria
      });
    },

    onSelectArticulo(articuloData: any) {
      if (!this.selected_movement) return;

      const articulo = new Articulo(articuloData);
      this.selected_movement.articulo = articulo.id;
      this.selected_movement.articulo_data = articulo;
      this.selected_movement.codename = articulo.codename;
      this.selected_movement.description = articulo.description;
      this.selected_movement.img = articulo.img || vars.IMG_ARTICULO;
      this.selected_movement.impuesto_value = articulo.impuesto?.value || 0;
      this.selected_movement.pendingServerSave = true;

      if (!this.selected_movement.precio_introduccido || this.selected_movement.precio_introduccido === 0) {
        this.selected_movement.precio_introduccido = articulo.latest_price || articulo.precio_max || 0;
      }

      this.onChangeMovimiento(this.selected_movement, true);

      // Cerrar el modal manualmente
      this.closeModal('articuloSelectModal');

      // Actualizar sugerencias después de agregar un artículo
      this.fetchSuggestions();

      this.selected_movement = null;
    },

    onOpenArticuloModal(movement = null as Movement | null) {
      if (!movement) {
        movement = this.createDraftMovement();
      }
      this.selected_movement = movement;
      this.openModal('articuloSelectModal');
    },

    openMovementDescriptionModal(movement: Movement) {
      this.selected_movement = movement;
      this.openModal('movementDescriptionModal');
    },

    onOpenPersonaModal() {
      this.openModal('personaSelectModal');
    },

    onOpenPaymentsModal() {
      this.openModal('paymentsModal');
    },

    openSendEmailModal() {
      this.openModal('sendEmailModal');
    },

    /**
     * Envía el documento por correo electrónico usando la API.
     */
    async submitSendEmail(): Promise<void> {
      if (!this.document) return;

      this.documentEmailSending = true;

      // El backend en esta vista espera un formData
      const formData = new FormData(this.$refs.documentSendEmailForm as HTMLFormElement);

      // Validar los datos
      if (!formData.get('recipient')) {
        this.handleErrors({ detail: [t('please provide a recipient email address')] });
        this.documentEmailSending = false;
        return;
      }
      if (!formData.get('subject')) {
        this.handleErrors({ detail: [t('please provide an email subject')] });
        this.documentEmailSending = false;
        return;
      }

      try {
        const response = await apiAxios.post(this.context!.apiUrls.documentoSendMail, formData);

        notifyMe({
          title: t('email sent', true),
          body: t('the email has been sent successfully'),
          timeout: 5000,
        });

        this.closeModal('sendEmailModal');

      } catch (error: any) {
        console.error('Error sending email:', error);
        this.handleErrors(error.response.data);
      } finally {
        this.documentEmailSending = false;
      }
    },

    onOpenAttachmentsModal() {
      this.openModal('attachmentsModal');
    },

    async onAttachmentSelected(event: any) {
      if (!this.document) return;

      const files = event.target.files;
      if (!files || files.length === 0) return;

      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append("file", files[i]); // 👈 el campo correcto
        formData.append("name", files[i].name);
        formData.append("description", files[i].name); // opcional

        try {
          const response = await apiAxios.post(this.context!.apiUrls.attachedForm,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );

          console.log("Archivo subido:", response.data.file);
        } catch (error: any) {
          console.error("Error subiendo archivo:", error);
        }
      }

      // refresca documento después de subir todos
      await this.fetchDocument();
    },

    async removeAttachment(file_id: number) {
      if (!this.document) return;

      console.log("Eliminando archivo con ID:", file_id);

      try {
        const formData = new FormData();
        formData.append("file_id", file_id.toString());
        const response = await apiAxios.post(this.context!.apiUrls.attachedDelete, formData);
        console.log("Archivo eliminado:", response.data);
        // refresca documento después de eliminar
        await this.fetchDocument();
      } catch (error: any) {
        console.error("Error eliminando archivo:", error);
      }
    },

    async removePayment(transaccion: Transaction) {
      if (!this.document) return;

      // Si no tiene ID, es un pago no guardado, solo eliminarlo del array local
      if (!transaccion.id) {
        this.document.transactions = this.document.transactions.filter(t => t !== transaccion);
        return;
      }

      console.log("Eliminando pago con ID:", transaccion.id);
      try {
        await apiAxios.delete(this.context!.apiUrls.transaccion + transaccion.id + '/');
        // Actualiza estado local
        await this.fetchDocument();
      } catch (error: any) {
        console.error('Error deleting payment:', error);
        this.handleErrors(error.response.data);
      }
    },

    async onDocumentMonedaChange() {
      if (!this.document) return;

      await this.$nextTick();

      // Actualizar la tasa de cambio automáticamente al cambiar la moneda
      const selectedMoneda = this.monedas?.find(m => m.id === this.document!.moneda);
      if (selectedMoneda) {
        this.document.moneda_tasa = selectedMoneda.tasa;
      }

      this.onDocumentChangeValue();
    },

    async onDocumentChangeValue() {
      if (!this.document) return;

      await this.$nextTick();
      if (!this.canChange || this.documentUpdatingFromBackend) return;

      this.documentPendingServerSave = true;
    },

    /**
     * TODO: Por compatibilidad con versiones de NCF convencional.
     * Imprime el documento actual mediante la API de impresión.
     */
    async onPrintDocumento() {
      if (!this.document) return;
      const url = this.context!.apiUrls.documentoPrint;
      try {
        const response = await apiAxios.post(url, {
          id: this.document.id,
          template_name: null,
          include_local_amount: this.document!.q_total != this.document!.q_total_local,
        });

        if (response.data.url) {
          this.fetchDocument();  // Refrescar documento
          window.open(response.data.url, '_blank');
        } else {
          console.error('No se recibió URL de impresión del documento.');
        }
      } catch (error: any) {
        console.error('Error printing document:', error);
        this.handleErrors(error.response.data);
      }
    },

    openEncfRequestModal() {
      this.openModal('encfRequestModal');
    },

    async submitEncfRequest() {
      if (!this.document) return;

      this.documentLoading = true;

      this.closeModal('encfRequestModal');

      console.log('Submitting ENCF request for document');

      try {
        const response = await apiAxios.post(this.context!.apiUrls.dgiiRequestEncf, {
          document_id: this.document.id,
        });
        console.log('ENCF request response:', response.data);  // {ok: true, task_id: 'xxxx'}

        // HACK: marcar manualmente el estado como REQUESTED para actualizar la interfaz inmediatamente
        this.document.ncf = this.document.ncf || {} as any;
        this.document!.ncf!.status = this.document.ncf!.status || {} as any;
        this.document!.ncf!.status!.internal_status = vars.REQUESTED;

        setTimeout(async () => {
          await this.fetchDocument();
        }, 5000);  // Esperar un poco antes de recargar para dar tiempo al backend

      } catch (error: any) {
        console.error('Error requesting ENCF:', error);
        this.handleErrors(error.response.data);
      } finally {
        this.documentLoading = false;
      }
    },

    openReturnsListModal() {
      this.openModal('returnsListModal');
    },

    openCancelDocumentModal() {
      if (!this.document) return;

      this.cancelDocumentReason = '';
      this.cancelDocumentSubtotal = new Decimal(0);
      this.cancelDocumentDiscount = new Decimal(0);
      this.cancelDocumentTax = new Decimal(0);
      this.cancelDocumentTotal = new Decimal(0);

      this.cancelDocumentItems = this.document.movements.map(mov => {
        const cantidadDisponible = Math.max(mov.cantidad - mov.get_cantidad_devuelta, 0);
        return {
          invoiceMovement: mov,
          cantidad: cantidadDisponible,
          disponible: cantidadDisponible,  // Límite máximo para anular
        }
      });

      // Calcular totales iniciales
      this.onCancelDocumentItemChange(null);

      this.openModal('cancelDocumentModal');
    },

    onCancelDocumentItemChange(item: any): void {
      /** Calcula totales de anulación */
      let subtotal = new Decimal(0);
      let discount = new Decimal(0);
      let tax = new Decimal(0);

      for (const it of this.cancelDocumentItems) {
        // Lo mejor es obtener el precio a partir del total original
        const total_sin_impuesto = new Decimal(it.invoiceMovement.total).dividedBy(
          new Decimal(1).plus(new Decimal(it.invoiceMovement.impuesto_value).dividedBy(100))
        );
        const precio_unitario = total_sin_impuesto.dividedBy(it.invoiceMovement.cantidad);
        const itemTotal = precio_unitario.times(it.cantidad);
        subtotal = subtotal.plus(itemTotal);

        // Descuento
        const itemDiscount = itemTotal
          .times(it.invoiceMovement.descuento_resultante)
          .dividedBy(100);
        discount = discount.plus(itemDiscount);

        // Impuesto
        const itemTax = itemTotal
          .times(it.invoiceMovement.impuesto_value)
          .dividedBy(100);
        tax = tax.plus(itemTax);
      }

      this.cancelDocumentSubtotal = subtotal;
      this.cancelDocumentDiscount = discount;
      this.cancelDocumentTax = tax;
      this.cancelDocumentTotal = subtotal.plus(tax);
    },

    /**
     * Anula el documento actual mediante una solicitud a la API.
     * La anulación genera un documento de devolución asociado y el backend valida
     * toda la lógica de negocio correspondiente.
     * El proceso consiste en crear la devolución y emitir el NCF resultante.
     * Si el documento no posee NCF electrónico, la anulación se ejecuta de inmediato.
     */
    async submitCancelDocument() {
      if (!this.document) return;

      this.cancelDocumentSaving = true;

      try {
        const url = this.context!.apiUrls.documento + this.document.id + '/cancel/';

        const items = this.cancelDocumentItems.map(item => ({
          movimiento_id: item.invoiceMovement.id,
          cantidad: item.cantidad,
        }));

        await apiAxios.post(url, {
          reason: this.cancelDocumentReason,
          items: items,
        });

        // En este punto la devolución fue creada pero es posible que la emisión
        // del NCF electrónico esté aun en proceso o tal vez fallida, dado que es asíncrona.
        this.documentLoading = true;
        await this.fetchDocument();

        // Cerramos el modal de cancelación y abrimos el que muestra la devolución creada
        this.closeModal('cancelDocumentModal');
        this.openModal('returnsListModal');

      } catch (error: any) {
        console.error('Error anulando documento:', error);
        this.handleErrors(error.response.data);
      } finally {
        this.documentLoading = false;
        this.cancelDocumentSaving = false;
      }
    },

    openDeleteDocumentModal() {
      this.openModal('deleteDocumentModal');
    },

    async submitDeleteDocument() {
      if (!this.document) return;
      this.documentLoading = true;
      try {
        await apiAxios.delete(this.context!.apiUrls.documento + this.document.id + '/');
        // Redirigir al listado después de eliminar
        window.location.href = this.context!.htmlUrls.documentoAppList;
      } catch (error: any) {
        console.error('Error deleting document:', error);
        this.handleErrors(error.response.data);
      } finally {
        this.documentLoading = false;
      }
    },

    onAddPayment() {
      const newPayment = this.createDraftPayment();
      if (newPayment && this.document) {
        if (!this.document.transactions) {
          this.document.transactions = [];
        }
        this.document.transactions.push(newPayment);
      }
    },

    createDraftPayment(): any | null {
      if (!this.document || !this.formas_de_pago) return null;
      const paymentData = {
        pendingServerSave: true,
        id: null,
        fecha: new Date().toISOString().split('T')[0],
        forma_de_pago: this.formas_de_pago.length > 0 ? this.formas_de_pago[0].id : null,
        forma_de_pago_nombre: this.formas_de_pago.length > 0 ? this.formas_de_pago[0].name : '',
        monto: this.document.balance || 0,
      };
      return new Transaction(paymentData);
    },

    onChangePayment(transaction: Transaction) {
      if (!this.document) return;
      transaction.pendingServerSave = true;
      // Realizar un cálculo del total pagado en el documento
      const totalPagado = this.document.transactions.reduce((sum, t) => sum + (t.monto || 0), 0);
      this.document.payments = totalPagado;
    },

    /**
     * Guarda un pago (transacción) asociado al documento actual.
     * El guardado se realiza mediante la API de transacciones.
     */
    async savePayment(transaction: Transaction): Promise<void> {
      if (!this.document) return;
      try {
        const payload = transaction.toApiPayload(this.document);
        const urlBase = this.context!.apiUrls.transaccion;
        const url = transaction.id ? `${urlBase}${transaction.id}/` : urlBase;
        const method = transaction.id ? apiAxios.put : apiAxios.post;
        const response = await method(url, payload);
        // Actualiza estado local
        await this.fetchDocument();
      } catch (error: any) {
        console.error('Error saving payment:', error);
        this.handleErrors(error.response.data);
      }
    },

    createDraftMovement(): Movement | null {
      if (!this.document) return null;
      const movementData = {
        pendingServerSave: true,
        id: null,
        url: '',
        img: vars.IMG_ARTICULO,
        numero: this.document.movements.length + 1,
        articulo: null,
        codename: '',
        description: '',
        cantidad: 1,
        precio: 0,
        precio_introduccido: 0,
        descuento: 0,
        descuento_resultante: 0,
        impuesto: 0,
        impuesto_value: 0,
        impuesto_ya_incluido: false,
        total: 0,
        total_local: 0,
        get_cantidad_devuelta: 0,
        disponible: 0,
      };
      return new Movement(movementData);
    },

    onAddMovement() {
      if (!this.document) return;
      const newMovement = this.createDraftMovement();
      if (newMovement) {
        this.document.movements.push(newMovement);
      }
      // Abrir el modal para seleccionar artículo
      this.onOpenArticuloModal(newMovement);
    },

    onRemoveMovimiento(index: number) {
      if (!this.document) return;

      // Crear una copia completa del documento para evitar mutaciones directas
      const movements = [...this.document.movements];
      movements.splice(index, 1);
      this.document.movements = movements;

      this.onCalculateTotals();
      this.documentPendingServerSave = true;

      // Actualizar sugerencias después de eliminar un movimiento
      this.fetchSuggestions();
    },

    onChangeMovimientoDescription(mov: Movement) {
      mov.pendingServerSave = true;
      this.documentPendingServerSave = true;
    },

    onChangeMovimientoCantidad(mov: Movement) {
      this.onChangeMovimiento(mov);
    },

    onChangeMovimientoPrecio(mov: Movement) {
      this.onChangeMovimiento(mov);
    },

    onChangeMovimientoDescuento(mov: Movement) {
      this.onChangeMovimiento(mov);
    },

    onPricesIncludeTaxChange() {
      this.onCalculateTotals();
      this.documentPendingServerSave = true;

      // Guardar en el localStorage la preferencia del usuario (con delay porque en este momento aun no se ha actualizado el checkbox)
      setTimeout(() => {
        localStorage.setItem(this.withPrefixKey('pricesIncludeTax'), this.pricesIncludeTax ? 'true' : 'false');
      }, 500);
    },

    async onChangeMovimiento(mov: Movement, value: boolean = true) {
      mov.calculateTotals(this.pricesIncludeTax);
      this.onCalculateTotals();
      mov.pendingServerSave = value;
      this.documentPendingServerSave = true;
    },

    canChangeThisMovement(mov: Movement): boolean {
      // Si el formulario está deshabilitado, no se puede cambiar
      if (this.isFormDisabled) return false;

      // Si el movimiento está pendiente de guardar en el servidor, se puede cambiar
      if (mov.pendingServerSave) return true;

      // Continuar con las validaciones generales
      return this.canChangeMovement;
    },

    onSearchPersonas() {
      if (this.personas_search_debounce_timer) {
        clearTimeout(this.personas_search_debounce_timer);
      }
      this.personas_search_debounce_timer = window.setTimeout(() => {
        this.searchPersonas(this.personas_search_query, 50);
      }, 500);
    },

    async searchPersonas(q: string, limit: number = 50) {
      this.personas_search_loading = true;
      try {
        const params = {
          q: q,
          activo: true,
          limit: limit,  // Importante: Limitar resultados para evitar sobrecargar la interfaz
        } as Record<string, any>;

        // Si el documento es una factura a crédito, filtrar solo personas con crédito activo
        if (this.document && this.document.tipo_data.clase === this.vars.FACTURA_CREDITO) {
          params.credito_activo = true;
        }

        // Si el documento es una compra, filtrar solo personas que son proveedores
        if (this.document && this.document.tipo_data.clase === this.vars.COMPRA) {
          params.is_suplidor = true;
        }

        const response = await apiAxios.get(this.context!.apiUrls.persona, {
          params: params
        });
        const data = response.data.results ? response.data.results : response.data;
        this.personas = data.map((data: any) => new Persona(data));
      } catch (error) {
        console.error('Error searching personas:', error);
        this.personas = [];
      } finally {
        this.personas_search_loading = false;
      }
    },

    onSelectPersona(persona: Persona) {
      if (!this.document) return;
      this.document.persona = persona.id;
      this.document.persona_data = persona;
      this.document.persona_nombre = persona.firstname;
      this.documentPendingServerSave = true;

      // Establecer la fecha de vencimiento según los términos de crédito del cliente
      if (persona.credito_activo && persona.credito_tiempo && this.document.fecha) {
        const fecha = new Date(this.document.fecha);
        fecha.setDate(fecha.getDate() + persona.credito_tiempo);
        const yyyy = fecha.getFullYear();
        const mm = String(fecha.getMonth() + 1).padStart(2, '0');
        const dd = String(fecha.getDate()).padStart(2, '0');
        this.document.fecha_vence = `${yyyy}-${mm}-${dd}`;
      }

      // El documento es guardado automáticamente al seleccionar la persona
      this.saveDocument();

      // Actualizar sugerencias después de seleccionar una persona
      this.fetchSuggestions();

      // Cerrar el modal manualmente
      this.closeModal('personaSelectModal');
    },


    /**
     * Obtiene un documento desde la API usando su ID.
     * @param id - ID del documento a recuperar
     */
    async fetchDocument(id: number | null = null): Promise<void> {
      if (!id) {
        id = this.context!.id;
      }

      // Reinicia errores previos
      this.resetErrors();

      this.documentLoading = true;
      try {
        this.document = await Document.fetch(id!);
        this.onCalculateTotals();
      } catch (error) {
        console.error('Error fetching document:', error);
        throw error;
      } finally {
        this.documentLoading = false;
      }
    },

    /**
     * Fetch lite document + simulación de progreso para carga en background.
     * Comentarios en español; código en English.
     */
    async fetchDocumentLite(id: number): Promise<void> {
      try {
        const response = await apiAxios.get(this.context!.apiUrls.documento + id + '/lite/');

        if (!this.document) this.document = {} as Document;
        Object.assign(this.document, response.data);

        this.document.isLite = true;
        this.document.movements ||= [];
        this.document.transactions ||= [];
        this.document.devoluciones ||= [];
        this.document.files ||= [];
        this.document.task_results ||= [];
        this.document.ncf ||= null;
        this.document.taxes_breakdown ||= {};
        this.document.persona_data ||= null;

        // Iniciar simulación de progreso

        // Limpiar cualquier intervalo previo
        if (this.documentMovementsFetchProgressInterval) {
          clearInterval(this.documentMovementsFetchProgressInterval);
          this.documentMovementsFetchProgressInterval = null;
        }

        // Reiniciar progreso
        this.documentMovementsFetchProgress = 0;
        const totalMovements = this.document.q_cantidad_movimientos || 0;

        // Si hay pocos movimientos, no simular progreso
        if (totalMovements < 10) {
          this.documentMovementsFetchProgress = null;
          return;
        }

        const hasConcluded = () => !this.document || !this.document.isLite;

        /**
         * Simulación por convergencia:
         * siempre avanza hacia un objetivo que es el 50% del restante.
         * Se fuerza el "snap" al objetivo para evitar quedar asintótico.
         */
        const startProgressiveSimulation = () => {
          let target = 70;
          let tickMs = 100;

          const tick = () => {
            if (hasConcluded()) {
              this.documentMovementsFetchProgress = null;
              clearInterval(this.documentMovementsFetchProgressInterval!);
              this.documentMovementsFetchProgressInterval = null;
              return;
            }

            const current = this.documentMovementsFetchProgress;

            if (current >= 99) return;

            const remaining = target - current;

            // Si estamos suficientemente cerca, forzar llegada al objetivo
            if (remaining <= 0.3) {
              this.documentMovementsFetchProgress = +target.toFixed(2);

              // recalcular siguiente objetivo y desacelerar
              target = target + (100 - target) * 0.5;
              tickMs = Math.min(1000, tickMs * 1.5);  // máximo 1s

              clearInterval(this.documentMovementsFetchProgressInterval!);
              this.documentMovementsFetchProgressInterval = setInterval(tick, tickMs);
              return;
            }

            // avance proporcional estable (sin comportamiento asintótico)
            const step = Math.max(0.3, remaining * 0.2);
            this.documentMovementsFetchProgress = Math.min(
              99,
              +(current + step).toFixed(2)
            );
          };

          this.documentMovementsFetchProgressInterval = setInterval(tick, tickMs);
        };

        startProgressiveSimulation();
      } catch (err) {
        if (this.documentMovementsFetchProgressInterval) {
          clearInterval(this.documentMovementsFetchProgressInterval);
          this.documentMovementsFetchProgressInterval = null;
        }
        this.documentMovementsFetchProgress = null;
        throw err;
      }
    },

    /**
     * Obtiene la lista de documentos desde la API,
     * aplicando filtros, búsqueda y paginación.
     */
    async fetchDocumentList(): Promise<void> {
      this.documentLoading = true;
      try {
        const params: Record<string, any> = {};

        // Filtros
        for (const [key, value] of Object.entries(this.listFilter)) {
          if (value !== null && value !== '') {
            params[key] = value;
          }
        }

        // Filtrar por clase de documento según el contexto
        // IMPORTANTE: los tipos 'FACTURA' y 'CONTABILIDAD' son especiales y agrupan varios tipos reales
        const context_doc_type_class = this.context!.doc_type_class?.toUpperCase() || '';
        let doc_type_classes = [];
        if (context_doc_type_class === 'FACTURA') {
          doc_type_classes = ['FACTURA_CONTADO', 'FACTURA_CREDITO'];
        } else if (context_doc_type_class === 'CONTABILIDAD') {
          doc_type_classes = ['CONTABILIDAD_DEBITO', 'CONTABILIDAD_CREDITO'];
        } else if (context_doc_type_class === 'CONTEO') {
          doc_type_classes = ['CONTEO_FISICO'];
        } else {
          doc_type_classes = [context_doc_type_class];
        }
        params['tipo_clase_in'] = doc_type_classes.join(','); // -> ?tipo_clase_in=FACTURA_CONTADO,FACTURA_CREDITO

        // Búsqueda
        if (this.list_search) {
          params['q'] = this.list_search;
        }

        // Ordenamiento
        if (this.listOrdering.length > 0) {
          params['ordering'] = this.listOrdering.join(',');
        }

        const response = await apiAxios.get(this.context!.apiUrls.documento+'list', { params });

        const results = response.data.results.map((data: any) => new DocumentLite(data));

        this.listPagination = {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          results: results,
        }

        // Actualizar la URL del navegador con los filtros activos
        this.updateUrlWithListFilters();

      } catch (error: any) {
        // Manejar caso especial de 404 sin resultados
        if (error.response?.status === 404 && !error.response?.data?.results) {
          this.listPagination.results = [];
          this.listFilter.page = 1; // resetear a página 1
        } else {
          console.error('Error fetching document list:', error);
          throw error;
        }
      } finally {
        this.documentLoading = false;
      }
    },

    async onListPaginationChangePage(page: number) {
      this.listFilter.page = page
      await this.fetchDocumentList()
    },

    async onListPaginationChangePagePrevious() {
      if (this.listPagination.previous) {
        this.listFilter.page = this.listFilter.page - 1;
        await this.fetchDocumentList();
      }
    },

    async onListPaginationChangePageNext() {
      if (this.listPagination.next) {
        this.listFilter.page = this.listFilter.page + 1;
        await this.fetchDocumentList();
      }
    },

    async onSearchInput() {
      if (this.list_search_debounce_timer) {
        clearTimeout(this.list_search_debounce_timer);
      }

      // Resetear a página 1 al buscar
      this.listFilter.page = 1;

      this.list_search_loading = true;
      this.list_search_debounce_timer = window.setTimeout(async () => {
        await this.fetchDocumentList();
        this.list_search_loading = false;
      }, 500);
    },

    applyListFilters() {
      this.fetchDocumentList();
    },

    resetListFilters() {
      this.listFilter = {
        page_size: this.listPaginationPageSize,
        page: 1,
        fecha_from: null,
        fecha_to: null,
        numero: null,
        tipo: null,
        // tipo_clase: null,  No se usa porque ya se filtra en el backend según la URL
        persona: null,
        almacen: null,
        create_user: null,
        ncf: null,
        tipo_comprobante_fiscal: null,
      };
      this.list_search = '';
      this.fetchDocumentList();
    },

    /**
     * Actualiza la URL del navegador para reflejar los filtros activos
     * del listado (sin recargar la página).
     * Esto permite compartir enlaces con los filtros aplicados.
     */
    updateUrlWithListFilters() {
      console.debug('Updating URL with list filters');
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(this.listFilter)) {
        if (value !== null && value !== '') {
          params.append(key, String(value));
        }
      }
      if (this.list_search && this.list_search.trim() !== '') {
        params.append('search', this.list_search.trim());
      }
      const newUrl = window.location.pathname + '?' + params.toString();
      window.history.replaceState({}, '', newUrl);
    },

    /**
     * Guarda el documento actual en el servidor.
     * Si existe un ID, se actualiza; de lo contrario, se crea uno nuevo.
     * Maneja errores de validación de forma estructurada.
     */
    async saveDocument() {
      if (!this.document) return;

      // Reinicia errores previos
      this.resetErrors();

      // Validar localmente antes de enviar
      try {
        this.document.validate();
      } catch (err) {
        if (err instanceof ValidationError) {
          this.documentErrors = err.errors;
          console.error('Validation errors:', err.errors);
          return;
        }
      }

      const urlBase = this.context!.apiUrls.documento;
      const payload = this.document.toApiPayload();

      this.documentSaving = true;
      this.documentUpdatingFromBackend = true;

      console.log('Saving document with payload:', payload);

      try {
        const url = this.document.id ? `${urlBase}${this.document.id}/` : urlBase;
        const method = this.document.id ? apiAxios.put : apiAxios.post;
        const response = await method(url, payload);

        // Actualiza estado local
        this.document = new Document(response.data);
        this.documentPendingServerSave = false;
        this.resetErrors();

      } catch (error: any) {
        this.handleErrors(error.response.data);
      } finally {
        this.documentSaving = false;
        this.documentUpdatingFromBackend = false;
      }
    },

    saveDocumentWithDebounce(delay: number = 2000) {
      if (this.documentSavingDebounceTimer) {
        clearTimeout(this.documentSavingDebounceTimer);
      }
      this.documentSavingDebounceTimer = window.setTimeout(() => {
        this.saveDocument();
      }, delay);
    },

    handleErrors(apiData: any) {
      // Reinicia errores previos
      this.resetErrors();

      const parsedErrors = parseErrors(apiData);
      console.log('Parsed Errors:', parsedErrors);

      // Clasificación de errores por tipo
      for (const [field, messages] of Object.entries(parsedErrors)) {
        if (field.startsWith('movements.')) {
          const [_, index, ...rest] = field.split('.');
          const movField = rest.join('.') || 'non_field_errors';

          if (!this.movementErrors[index]) {
            this.movementErrors[index] = {};
          }
          this.movementErrors[index][movField] = messages;
        } else {
          this.documentErrors[field] = messages;
        }
      }
      // Mostrar los toasts de errores (reintentar visibilidad)
      // Usamos showToasts() para invocar la API de Bootstrap y forzar que
      // los toasts se muestren aunque el usuario haya cerrado el toast previo.
      this.showToasts();
    },

    resetErrors() {
      this.documentErrors = {};
      this.movementErrors = {};
    },

    /**
     * Muestra (o vuelve a mostrar) todos los toasts presentes en el contenedor
     * utilizando la API de Bootstrap. Usamos $nextTick para asegurarnos de que
     * el DOM haya reflejado los últimos cambios en `documentErrors` / `movementErrors`.
     */
    showToasts() {
      this.$nextTick(() => {
        try {
          const BootstrapToast = (window as any).bootstrap && (window as any).bootstrap.Toast;
          if (!BootstrapToast) return;

          const container = document.querySelector('.toast-container');
          if (!container) return;

          const toastEls = container.querySelectorAll('.toast');
          toastEls.forEach((el) => {
            try {
              const instance = (BootstrapToast.getOrCreateInstance)
                ? BootstrapToast.getOrCreateInstance(el)
                : new BootstrapToast(el);

              // Capturar cierre del toast
              el.addEventListener('hidden.bs.toast', () => {
                this.onToastClosed(el);
              });

              instance.show();
            } catch (e) {
              console.error('Error mostrando toast:', e);
            }
          });
        } catch (e) {
          console.error('showToasts error:', e);
        }
      });
    },

    onToastClosed(toastEl: Element) {
      const el = toastEl as HTMLElement;
      const indexStr = el.dataset.index || null;
      const index = indexStr !== null ? Number(indexStr) : null;

      if (index !== null) {
        // Toast de movimiento
        if (this.movementErrors[index]) {
          this.movementErrors.splice(index, 1);
        }
        return;
      }

      // Toast de documento
      for (const key in this.documentErrors) {
        delete this.documentErrors[key];
      }
    },


    // Llama a `calculateTotals` después de un tick para asegurar que
    // todos los cambios en el DOM y datos reactivos se hayan aplicado.
    async onCalculateTotals() {
      if (!this.document) return;
      await this.$nextTick();

      this.resetErrors();  // Resetear errores
      this.documentUpdatingFromBackend = true;  // Bloqueo de guardado automático
      this.document.calculateTotals();
      this.documentUpdatingFromBackend = false;  // Fin del bloqueo de guardado automático
    },

    /**
     * Obtiene sugerencias de productos basadas en el cliente y productos actuales.
     */
    async fetchSuggestions() {
      if (!this.document) return;

      // Solo estas clases de documento tienen sugerencias
      const clasesConSugerencias = [
        this.vars.FACTURA_CONTADO,
        this.vars.FACTURA_CREDITO,
      ];
      if (!clasesConSugerencias.includes(this.document.tipo_data.clase)) {
        this.showSuggestions = false;
        return;
      }

      this.suggestionsLoading = true;

      console.log('Fetching invoice suggestions for client ID:', this.document.persona);

      try {
        const selectedItems = this.document.movements
          .filter(m => m.articulo)
          .map(m => m.articulo);

        const response = await apiAxios.post('/ai/invoice-suggestions/', {
          client_id: this.document.persona,
          selected_items: selectedItems,
          limit: 5,
        });

        // Recorrer las sugerencias y obtener los artículos correspondientes usando el articulo_id
        const suggestions = response.data.suggestions || [];
        this.suggestions = suggestions
          .map((sugg: any) => {
            const articulo = this.articulos!.find(art => art.id === sugg.articulo_id);
            if (!articulo) return null;
            return {
              ...sugg,
              articulo: articulo,
              product_name: articulo.description,
            };
          })
          .filter(Boolean);  // Filtrar sugerencias sin artículo encontrado

        this.showSuggestions = this.suggestions.length > 0;
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        this.suggestions = [];
      } finally {
        this.suggestionsLoading = false;
      }
    },

    /**
     * Agrega una sugerencia como nuevo movimiento.
     */
    onSelectSuggestion(suggestion: any) {
      if (!this.document) return;

      // Si la sugerencia tiene un articulo_id, buscar el artículo y usar onSelectArticulo
      if (suggestion.articulo_id && this.articulos) {
        const articulo = this.articulos.find(a => a.id === suggestion.articulo_id);
        if (articulo) {
          const newMovement = this.createDraftMovement();
          if (newMovement) {
            this.document.movements.push(newMovement);
            this.selected_movement = newMovement;
            this.onSelectArticulo(articulo);
          }
          return;
        }
      }

      // Actualizar sugerencias después de agregar
      this.fetchSuggestions();
    },

    /**
     * Cierra el panel de sugerencias.
     */
    closeSuggestions() {
      this.showSuggestions = false;
    },

    openModal(modalId: string) {
      const modalElement = document.getElementById(modalId);
      if (modalElement) {
        const modalInstance = new bootstrap.Modal(modalElement);
        modalInstance.show();
      }
    },

    closeModal(modalId: string) {
      const modalElement = document.getElementById(modalId);
      if (modalElement) {
        const modalInstance = bootstrap.Modal.getInstance(modalElement);
        modalInstance?.hide();
      }
    },

    loadLocalStoragePreferences() {
      // Cargar preferencia de precios con impuesto incluido desde localStorage
      this.pricesIncludeTax = localStorage.getItem(this.withPrefixKey('pricesIncludeTax')) === 'true';

      // Cargar columnas visibles desde localStorage (listado de strings)
      this.loadListConfigVisibleColumnsToLocalStorage();
    },

    loadListConfigVisibleColumnsToLocalStorage() {
       const storedColumns = localStorage.getItem(this.withPrefixKey('listConfig__visibleColumns'));
      if (storedColumns) {
        try {
          const visibleColumnsArray = JSON.parse(storedColumns);
          if (Array.isArray(visibleColumnsArray)) {
            this.listConfigVisibleColumns = visibleColumnsArray;
          }
        } catch (e) {
          console.error('Error parsing stored visible columns:', e);
        }
      }
    },

    saveListConfigVisibleColumnsToLocalStorage() {
      console.debug('Saving list config visible columns to localStorage');
      const visibleColumns = this.listConfigVisibleColumns;
      localStorage.setItem(
        this.withPrefixKey('listConfig__visibleColumns'),
        JSON.stringify(visibleColumns)
      );
    },


    /**
     * Agrega el prefijo único al nombre de una clave para localStorage y otros usos.
     */
    withPrefixKey(key: string): string {
      return `${this.getPrefixKey()}${key}`;
    },

    /**
     * Obtiene el prefijo único para las claves de localStorage y otros usos.
     * basado en el tipo de documento actual.
     */
    getPrefixKey(): string {
      return `document_${this.context.doc_type_class}__`;
    }

  },
});


window.DocumentFormApp = DocumentFormApp;

export default DocumentFormApp;